import { useCallback, useMemo, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { styled, useTheme } from '@mui/material/styles';
import { dateFormat } from '@bloomays-lib/utils.shared';
import { Avatar } from '@bloomays-lib/ui.shared';
import { useTranslation } from 'react-i18next';
import { DataGridV2 } from '@bloomays-lib/ui.shared/datagrid';
import type { DataGridV2Column, DataGridV2LegendItem } from '@bloomays-lib/ui.shared/datagrid';
import {
  CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID,
  type CLevelPrimesDashboardGridRow,
  type CLevelSynthesisRow,
  type CLevelSynthesisViewMode,
  type MonthIndex,
} from '../../types/primes';
import {
  buildCLevelSynthesisMonthNavigationTarget,
  createCLevelSynthesisExportFooterRow,
  getCLevelSynthesisCellDisplayValue,
  getCLevelMonthColumnExportDisabledReason,
  isCLevelSynthesisExportFooterRow,
  isCLevelSynthesisMonthCellNavigable,
} from '../../utils/clevelPrimesDashboard.utils';
import { downloadCLevelValidatedPrimesExport } from '../../utils/clevelPrimesExport.utils';
import { buildCLevelAnnualDetailPath } from '../../utils/annualPrime.utils';
import { formatPrimeEuro, formatPrimeEuroWithSign } from '../../utils/monthlyPrime.utils';
import { notify } from '../../helpers/toastify';

export type CLevelPrimesDashboardTableProps = {
  rows: CLevelSynthesisRow[];
  loading: boolean;
  selectedYear: string;
  viewMode: CLevelSynthesisViewMode;
  lifetimeDisplayAmountByTamEmail?: Record<string, string>;
  lifetimeMarginLoading?: boolean;
};

const MONTH_INDICES: MonthIndex[] = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];

const EXPORT_FOOTER_ROW = createCLevelSynthesisExportFooterRow();

const leftAlignedColumn = {
  align: 'left' as const,
  headerAlign: 'left' as const,
};

const formatSynthesisAmount = (value: number): string =>
  value < 0 ? formatPrimeEuroWithSign(value) : formatPrimeEuro(value);

const EXPORT_DISABLED_TOOLTIP_KEYS = {
  loading: 'clevelSynthesisExportDisabledLoadingTooltip',
  error: 'clevelSynthesisExportDisabledErrorTooltip',
  missingLuccaId: 'clevelSynthesisExportDisabledMissingLuccaIdTooltip',
  pending: 'clevelSynthesisExportDisabledPendingTooltip',
  noValidated: 'clevelSynthesisExportDisabledNoValidatedTooltip',
} as const;

const CLevelPrimesDashboardTable = ({
  rows,
  loading,
  selectedYear,
  viewMode,
  lifetimeDisplayAmountByTamEmail = {},
  lifetimeMarginLoading = false,
}: CLevelPrimesDashboardTableProps) => {
  const { t } = useTranslation(['primes']);
  const theme = useTheme();
  const navigate = useNavigate();
  const annualLoadErrorLabel = t('annualLoadError');

  const statusLegendItems: DataGridV2LegendItem[] = useMemo(
    () => [
      { label: t('clevelSynthesisLegendValidated'), tone: 'success' },
      { label: t('clevelSynthesisLegendPending'), tone: 'warning' },
      { label: t('clevelSynthesisLegendUpcoming'), tone: 'neutral' },
    ],
    [t],
  );

  const handleRowClick = useCallback(
    (row: CLevelPrimesDashboardGridRow) => {
      if (isCLevelSynthesisExportFooterRow(row)) {
        return;
      }
      navigate(buildCLevelAnnualDetailPath(row.tamEmail, selectedYear));
    },
    [navigate, selectedYear],
  );

  const renderMonthCellLabel = useCallback(
    (row: CLevelSynthesisRow, monthIndex: MonthIndex): string => {
      const cell = row.months[monthIndex];
      if (cell.status === 'loading') {
        return '…';
      }
      if (cell.status === 'error') {
        return annualLoadErrorLabel;
      }
      const displayValue = getCLevelSynthesisCellDisplayValue(cell, viewMode);
      if (cell.status === 'empty' || displayValue === null) {
        return '—';
      }
      return formatSynthesisAmount(displayValue);
    },
    [annualLoadErrorLabel, viewMode],
  );

  const renderMonthCellContent = useCallback(
    (row: CLevelSynthesisRow, monthIndex: MonthIndex) => {
      const cell = row.months[monthIndex];
      const label = renderMonthCellLabel(row, monthIndex);
      const tone =
        cell.status === 'value' && cell.valueSource
          ? cell.valueSource === 'validated'
            ? theme.palette.green[100]
            : theme.palette.warning.main
          : undefined;

      if (!isCLevelSynthesisMonthCellNavigable(row, monthIndex)) {
        return <MonthCellValue $tone={tone}>{label}</MonthCellValue>;
      }

      const target = buildCLevelSynthesisMonthNavigationTarget(row, monthIndex, selectedYear);
      return (
        <MonthCellLink
          to={{ pathname: target.pathname, search: target.search }}
          state={target.state}
          aria-label={t('annualActionMonthlyDetail')}
          $tone={tone}
          data-tone-color={tone}
          onClick={(event: MouseEvent<HTMLAnchorElement>) => event.stopPropagation()}
        >
          {label}
        </MonthCellLink>
      );
    },
    [renderMonthCellLabel, selectedYear, t, theme.palette.green, theme.palette.warning.main],
  );

  const renderExportMonthCell = useCallback(
    (monthIndex: MonthIndex) => {
      const disabledReason = getCLevelMonthColumnExportDisabledReason(rows, monthIndex);
      const enabled = disabledReason === null;
      const monthLabel = dateFormat(new Date(2026, Number(monthIndex) - 1, 1), 'MMMM');

      const handleExportClick = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        const exportResult = downloadCLevelValidatedPrimesExport(rows, monthIndex, selectedYear);
        if (!exportResult.downloaded) {
          notify('warning', t('clevelSynthesisExportEmpty'), undefined, {
            hideProgressBar: true,
            toastId: `clevel-export-empty-${monthIndex}-${selectedYear}`,
          });
        }
      };

      const exportButton = (
        <ExportIconButton
          size="small"
          disabled={!enabled}
          aria-label={t('clevelSynthesisExportMonth', { month: monthLabel, year: selectedYear })}
          onClick={handleExportClick}
        >
          <FileDownloadIcon fontSize="small" />
        </ExportIconButton>
      );

      if (disabledReason === null) {
        return exportButton;
      }

      return (
        <Tooltip title={t(EXPORT_DISABLED_TOOLTIP_KEYS[disabledReason])} arrow>
          <ExportButtonWrapper>{exportButton}</ExportButtonWrapper>
        </Tooltip>
      );
    },
    [rows, selectedYear, t],
  );

  const columns: DataGridV2Column<CLevelPrimesDashboardGridRow>[] = useMemo(
    () => [
      {
        field: 'tamDisplayName',
        header: t('tamFilterLabel'),
        minWidth: 220,
        flex: 1.2,
        sortable: false,
        variant: 'custom',
        renderPinnedRow: true,
        render: (row) => {
          if (isCLevelSynthesisExportFooterRow(row)) {
            return null;
          }

          const normalizedEmail = row.tamEmail.trim().toLowerCase();
          const lifetimeAmount = lifetimeDisplayAmountByTamEmail[normalizedEmail];
          const seniorityLabel = row.tamSeniority?.trim() || '—';
          const lifetimeCumulLabel = lifetimeMarginLoading
            ? '…'
            : lifetimeAmount
            ? t('clevelSynthesisTamLifetimeCumul', { amount: lifetimeAmount })
            : null;

          return (
            <TamCell>
              <Avatar size="small" alt={row.tamDisplayName} image={row.avatarUrl} />
              <TamTextBlock>
                <TamName>{row.tamDisplayName}</TamName>
                <TamSubtitleRow>
                  <TamSeniority>{seniorityLabel}</TamSeniority>
                  {lifetimeCumulLabel ? (
                    <>
                      <TamSubtitleSeparator aria-hidden="true">·</TamSubtitleSeparator>
                      <TamLifetimeCumul>{lifetimeCumulLabel}</TamLifetimeCumul>
                    </>
                  ) : null}
                </TamSubtitleRow>
              </TamTextBlock>
            </TamCell>
          );
        },
        ...leftAlignedColumn,
      },
      ...MONTH_INDICES.map((monthIndex) => ({
        field: `month-${monthIndex}` as const,
        header: dateFormat(new Date(2026, Number(monthIndex) - 1, 1), 'MMM'),
        minWidth: 90,
        flex: 0.7,
        sortable: false,
        filterable: false,
        variant: 'custom' as const,
        renderPinnedRow: true as const,
        render: (row: CLevelPrimesDashboardGridRow) =>
          isCLevelSynthesisExportFooterRow(row)
            ? renderExportMonthCell(monthIndex)
            : renderMonthCellContent(row, monthIndex),
        ...leftAlignedColumn,
      })),
    ],
    [
      lifetimeDisplayAmountByTamEmail,
      lifetimeMarginLoading,
      renderExportMonthCell,
      renderMonthCellContent,
      selectedYear,
      t,
    ],
  );

  return (
    <DataGridV2<CLevelPrimesDashboardGridRow>
      uniqueId="clevel-primes-dashboard-table"
      rows={rows}
      columns={columns}
      loading={loading}
      loadingVariant="bar"
      getRowId={(row) => row.rowId}
      onRowClick={handleRowClick}
      legend={statusLegendItems}
      search
      searchPlaceholder={t('clevelSynthesisSearchPlaceholder')}
      searchFields={['tamDisplayName']}
      noRowsLabel={t('clevelSynthesisSearchNoResults')}
      enableExport={false}
      enableColumnSelector={false}
      enableFilterPanel={false}
      pagination={rows.length > 12}
      pageSize={12}
      hideFooter={rows.length <= 12}
      muiProps={{
        pinnedRows: {
          bottom: [EXPORT_FOOTER_ROW],
        },
        sx: {
          [`& .MuiDataGrid-row:not([data-id="${CLEVEL_SYNTHESIS_EXPORT_FOOTER_ROW_ID}"])`]: {
            cursor: 'pointer',
          },
        },
      }}
    />
  );
};

export default CLevelPrimesDashboardTable;

const TamCell = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  minWidth: 0,
  width: '100%',
}));

const TamTextBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
}));

const TamName = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '13.5px',
  fontWeight: 500,
  color: theme.palette.neutral[90],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

const TamSubtitleRow = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  minWidth: 0,
  overflow: 'hidden',
}));

const TamSeniority = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12px',
  color: theme.palette.neutral[60],
  whiteSpace: 'nowrap',
  flexShrink: 0,
}));

const TamSubtitleSeparator = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Regular", sans-serif',
  fontSize: '12px',
  color: theme.palette.neutral[40],
  flexShrink: 0,
}));

const TamLifetimeCumul = styled('span')(({ theme }) => ({
  fontFamily: '"Poppins Medium", sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: theme.palette.purple[80],
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const MonthCellValue = styled('span', {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone?: string }>(({ $tone }) => ({
  color: $tone,
}));

const MonthCellLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== '$tone',
})<{ $tone?: string }>(({ theme, $tone }) => ({
  color: $tone ?? 'inherit',
  textDecoration: 'none',
  '&:hover': {
    color: $tone ?? theme.palette.primary.main,
    textDecoration: 'underline',
  },
}));

const ExportIconButton = styled(IconButton)(({ theme }) => ({
  '&.Mui-disabled': {
    opacity: 0.35,
  },
  color: theme.palette.primary.main,
}));

const ExportButtonWrapper = styled('span')(() => ({
  display: 'inline-flex',
}));
